import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [token, setToken] = useState(null);

  const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

  // Inactivity auto-logout
  useEffect(() => {
    if (!user) return;

    let timeoutId;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('[AuthContext] Inactivity timeout - logging out');
        performLogout();
        window.location.href = window.location.pathname.startsWith('/mobile') ? '/mobile/login' : '/login';
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  const performLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedTenantId');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setSelectedTenantId(null);
  };

  // Setup axios interceptor to add X-Tenant-Id header for Super-Admin and other users
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Add X-Tenant-Id header for Super-Admin if tenant is selected
        if (user?.role === 'super_admin') {
          if (selectedTenantId) {
            config.headers['X-Tenant-Id'] = selectedTenantId;
          } else if (user?.tenant_id) {
            // Fallback: use assigned tenant_id if no selection made
            config.headers['X-Tenant-Id'] = user.tenant_id;
          }
        }
        // For other roles (tenant_admin, warehouse_staff), use their assigned tenant_id
        else if (user?.tenant_id) {
          config.headers['X-Tenant-Id'] = user.tenant_id;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [user, selectedTenantId]);

  // Interceptor: auto-logout on 401 (expired token)
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user) {
          console.log('[AuthContext] 401 received - token expired, logging out');
          performLogout();
          window.location.href = window.location.pathname.startsWith('/mobile') ? '/mobile/login' : '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedTenantId = localStorage.getItem('selectedTenantId');
    
    if (storedToken && savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // AUTO-FIX: If user has tenant_id but no selectedTenantId, set it automatically
      if (userData.tenant_id && !savedTenantId) {
        console.log('[AuthContext] Auto-setting selectedTenantId from user.tenant_id:', userData.tenant_id);
        localStorage.setItem('selectedTenantId', userData.tenant_id);
        setSelectedTenantId(userData.tenant_id);
      } else if (savedTenantId) {
        setSelectedTenantId(savedTenantId);
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      
      // AUTO-SET selectedTenantId if user has tenant_id
      if (userData.tenant_id) {
        localStorage.setItem('selectedTenantId', userData.tenant_id);
        setSelectedTenantId(userData.tenant_id);
        console.log('[AuthContext] Auto-set selectedTenantId on login:', userData.tenant_id);
      }
      
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, name) => {
    try {
      await axios.post(`${API_URL}/auth/register`, { email, password, name });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    performLogout();
  };

  const selectTenant = (tenantId) => {
    setSelectedTenantId(tenantId);
    if (tenantId) {
      localStorage.setItem('selectedTenantId', tenantId);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, selectedTenantId, selectTenant }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
